import { FetchArgs, createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { piiConfig, searchFields } from "../../../../../config/constants";
import { IGraphGetDriveInfoFromSharepointIdRequest, IGraphGetUsersRequest, IGraphSearchSharePointResponse } from "./api-graph-interfaces";
import { shareId } from "../../../../../services/copilot/shareId";

const GRAPH_API_URL = 'https://graph.microsoft.com/v1.0';
/** How long one sendMail request may take. A throttled send is asked again, each try with this limit (Graph
 * answers a 429 at once, so in practice a send still ends within about a minute). */
export const SEND_MAIL_TIMEOUT_MS = 60000;
/** How often a throttled (429) sendMail is asked again. */
const SEND_MAIL_THROTTLING_RETRIES = 3;

/** The content manager's search query: the search text, the filter, and the sites OR'd together.
 * Exported so a search spanning a client group's sites can measure it against the KQL limit. */
export const buildSharePointSearchQueryString = (args: { searchValue: string, searchFilter?: string, siteUrlList?: string[] }): string => {
    const { searchValue, searchFilter, siteUrlList } = args;
    let queryString: string = searchFilter
        ? `(*${searchValue}*) ${searchFilter}`
        : `(*${searchValue}*)`;

    const siteUrls: string[] = Array.isArray(siteUrlList) ? siteUrlList.filter(siteUrl => siteUrl) : [];
    if (siteUrls.length > 0) {
        queryString += ` (${siteUrls
            .map(siteUrl => `site:\"${siteUrl}\"`)
            .join(" OR ")})`;
    }
    return queryString;
};

export interface ISearchSharePointArgs {
    searchValue: string;
    searchFilter?: string;
    siteUrlList?: string[];
    /** What to search for; the content manager's header search leaves it out and gets sites and files. */
    entityTypes?: string[];
    from?: number;
    size?: number;
    accessToken: string;
}

/** The request searchSharePoint sends. Exported so a test can read it without a store or a fetch. */
export const searchSharePointRequest = (args: ISearchSharePointArgs) => {
    const headers = new Headers();
    headers.set('Authorization', `Bearer ${args.accessToken}`);

    const { from, size } = args;
    const queryString: string = buildSharePointSearchQueryString(args);

    const body = {
        requests: [
            {
                entityTypes: args.entityTypes ?? [
                    "site",
                    "driveItem"
                ],
                query: {
                    queryString
                },
                fields: searchFields.graph,
                from: from ?? 0,
                size: size ?? 250
            }
        ]
    }

    return {
        url: `/search/query`,
        method: 'POST',
        body,
        headers
    }
};

const staggeredBaseQueryWithBailOut = retry(
    async (args: string | FetchArgs, api, extraOptions) => {
        const result = await fetchBaseQuery({
            baseUrl: GRAPH_API_URL,
            prepareHeaders: async (headers) => {
                return headers;
            }
        })(args, api, extraOptions)
        const errorStatus = result?.error?.status;
        if (errorStatus === 401 || errorStatus === 403) {
            retry.fail(result.error);
        }
        return result;
    },
    {
        maxRetries: 3,
    }
);

export const apiGraphSlice = createApi({
    reducerPath: 'api-graph',
    baseQuery: staggeredBaseQueryWithBailOut,
    endpoints: (builder) => ({
        getMetaDataFields: builder.query<any, { driveId: string, itemId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { driveId, itemId } = args;
                return {
                    url: `/drives/${driveId}/items/${itemId}/listItem?$select=sharepointIds&$expand=fields($select=${piiConfig.privacySentitiveColumnName},${piiConfig.privacySentitiveDataColumnName})`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getUsers: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                return {
                    url: `/users?$count=true&$search="displayName:${args.request.filter}" OR "mail:${args.request.filter}" OR "userPrincipalName:${args.request.filter}"&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getAllUsers: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string, top?: number }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                return {
                    url: `/users?$top=${args.top ? args.top : 10}&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getPeople: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                //url: `/me/people?$top30&$search=${args.request.filter}&$select=displayName,scoredEmailAddresses,personType,department,jobTitle,userPrincipalName&$filter=personType/subclass eq 'OrganizationUser' or personType/subclass eq 'Guest' or personType/subclass eq 'UnifiedGroup'`,
                return {
                    url: `/people?$count=true&$filter=startsWith(displayName,'${args.request.filter}') or startsWith(userPrincipalName,'${args.request.filter}') or startsWith(mail,'${args.request.filter}')&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getGroups: builder.query<any, { request: IGraphGetUsersRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);
                headers.set('ConsistencyLevel', `eventual`);

                return {
                    url: `/groups?$count=true&$filter=startsWith(displayName,'${args.request.filter}') or startsWith(mail,'${args.request.filter}')&$orderBy=${args.request.orderby}&$select=${args.request.select}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getDriveInfoFromSharepointId: builder.query<any, { request: IGraphGetDriveInfoFromSharepointIdRequest, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.request.driveId}/root/search(q='${args.request.sharepointId}')`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getItemWebUrl: builder.query<any, { driveId: string, itemId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.driveId}/items/${args.itemId}?$select=id,webUrl&$expand=listItem($select=webUrl)`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getItem: builder.query<any, { driveId: string, itemId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.driveId}/items/${args.itemId}`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getRecentFiles: builder.query<any, { accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/me/drive/recent?$select=remoteItem&$top=500`,
                    method: 'GET',
                    headers
                }
            },
            transformResponse: (response: any) => {
                const newResponse = [];

                response.value.forEach((item) => {
                    newResponse.push({
                        ...item.remoteItem,
                        col_name: item.remoteItem.name,
                        col_lastModifiedDateTime: item.remoteItem.lastModifiedDateTime,
                        col_size: item.remoteItem.size,
                        col_lastModifiedBy: item.remoteItem.lastModifiedBy.user.displayName,
                    })

                });

                return newResponse;
            },
        }),
        searchSharePoint: builder.query<IGraphSearchSharePointResponse, ISearchSharePointArgs>({
            query: searchSharePointRequest,
            transformResponse: (response: any) => {
                return { ...response.value[0].hitsContainers[0] } as IGraphSearchSharePointResponse;
            },
        }),
        getSiteIdFromDriveId: builder.query<any, { driveId: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                return {
                    url: `/drives/${args.driveId}?$select=list&$expand=list($select=parentReference)`,
                    method: 'GET',
                    headers
                }
            }
        }),
        getDriveItemBySharingUrl: builder.query<any, { sharingUrl: string, accessToken: string }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                // Graph's share id is the base64url of the URL's UTF-8 bytes, unpadded. btoa of the URL itself
                // gave other bytes for an accented name and threw on any other script (audit 2026-10-22).
                return {
                    url: `https://graph.microsoft.com/v1.0/shares/${shareId(args.sharingUrl)}/driveItem?$select=id,name,webUrl,sharepointIds,parentReference&$expand=listItem`,
                    method: 'GET',
                    headers
                }
            }
        }),
        sendMail: builder.query<any, { accessToken: string, html: string, subject: string, toRecipients: string[] }>({
            query: (args) => {
                const headers = new Headers();
                headers.set('Authorization', `Bearer ${args.accessToken}`);

                const { html, subject, toRecipients } = args;

                const body = {
                    "message": {
                        "subject": subject,
                        "body": {
                            "contentType": "HTML",
                            "content": html
                        },
                        "toRecipients": toRecipients.map(recipient => {
                            return {
                                "emailAddress": {
                                    "address": recipient
                                }
                            }
                        }),
                    }
                }

                return {
                    url: `https://graph.microsoft.com/v1.0/me/sendMail`,
                    method: 'POST',
                    body,
                    headers,
                    timeout: SEND_MAIL_TIMEOUT_MS
                }
            },
            // Sending is not idempotent: a replay after Graph accepted the first request sends the mail twice.
            // A 429 is the exception: Graph turned it away unprocessed (e.g. a fifth request at once to one
            // mailbox, which a Send of five files can make).
            extraOptions: { retryCondition: (error: any, _args: unknown, { attempt }: { attempt: number }) => (error?.status === 429 || error?.originalStatus === 429) && attempt <= SEND_MAIL_THROTTLING_RETRIES },
            // The share panel sends every file's mail through one lazy trigger, which lets go of the previous send
            // when it starts the next. RTK drops a let-go result keepUnusedDataFor later (60 s by default) even while
            // the send is still running, and then reports it as "uninitialized". Keep it longer than a send can take
            // (four tries of up to a minute).
            keepUnusedDataFor: 300
        })
    })
});

export const {
    useLazyGetMetaDataFieldsQuery,
    useLazyGetUsersQuery,
    useLazyGetAllUsersQuery,
    useLazyGetPeopleQuery,
    useLazyGetGroupsQuery,
    useLazyGetDriveInfoFromSharepointIdQuery,
    useLazyGetRecentFilesQuery,
    useLazySearchSharePointQuery,
    useLazyGetItemWebUrlQuery,
    useLazyGetSiteIdFromDriveIdQuery,
    useLazyGetItemQuery,
    useLazyGetDriveItemBySharingUrlQuery,
    useLazySendMailQuery
} = apiGraphSlice;